
/*
    Container for displaying a list of active queued messages,
    which can be individually expanded to show update (only one at a time),
    or multiple can be selected for mass operations 

    also has a filter for searching for message records, 
    and a form for adding new ones
*/

import React, { useContext } from "react";

import MainContext from "../MainContext";
import DataContext from "../DataContext";

import VerticalLayout from "../Components/VerticalLayout";
import SubNav from "../Components/SubNav";
import SingleOpenList from "../Components/SingleOpenList";

import MessageGeneralFilter from "../Filters/MessageGeneralFilter";
import MessageGeneralData from "../DataDisplay/MessageGeneralData";
import MessageGeneralForm from "../Forms/MessageGeneralForm";

import { addElement, replaceElement } from "../Scripts/Utilities";

import { getLocations } from "../Requests/UtilityRequests";

import { 
    getGeneralMessages, addGeneralMessage, updateGeneralMessage, deleteGeneralMessage, enableOrDisableMessage 
} from "../Requests/MessageRequests";

//
// wrapper component that just makes sure we can update immediately when the DataContext
// signals that we need to reload
//
const MessagesGeneralPageContainer = (props) => {
    const context = useContext(DataContext);

    return (
        <MessagesGeneralPage {...props}
            dataReload={context.dataReload}
            setReload={context.setReload}
        />
    )
};

export default MessagesGeneralPageContainer;


class MessagesGeneralPage extends React.Component {

    static contextType = MainContext;

    state = {
        filterValues: {},
        messages: [],
        selectedMessages: [],
        addMessageFormOpen: false,
        closeAll: false,
        locationLookup: null
    }

    //
    //
    getData = (callback) => {

        const request = () => getGeneralMessages({...this.state.filterValues, groupid: this.context.user.groupid});

        const then = ({data}) => {
            console.log("MESSAGE DATA", data);
            if(data.length){
                this.setState({messages: data}, () => {
                    if(callback && typeof callback === "function"){
                        callback();
                    }
                });
            }
            else {
                this.setState({Messages: []});
                this.context.sendNotification(true, "No records found for this filter");
            }
        };

        this.context.runProcessing(request, then, "getGeneralMessages");
    }

    //
    //
    componentDidMount(){
        getLocations()
        .then(({data}) => {
            // create the lookup object
            const lookup = {};
            data.forEach((loc) => {
                lookup[loc["bus_terminal_code"]] = loc["bus_terminal_name"];
            });

            console.log("LOCATION LOOKUP", lookup, data);

            this.setState({locationLookup: lookup}, () => {
                this.getData();
            });
        });
    }

    //
    // either the message form is opened and we need to close the filter,
    // or we need to reload
    //
    componentDidUpdate() {
        if(this.context.filterOpen && this.state.addMessageFormOpen){
            this.setState({addMessageFormOpen: false});
        }
        else if(this.props.dataReload){
            this.props.setReload(false, () => {                
                this.getData();
            });
        }
    }

    //
    //
    openMessageForm = () => {
        if(this.state.addMessageFormOpen){
            this.setState({addMessageFormOpen: false});
        }
        else{
            this.context.toggleFilter(null, false);
            this.setState({addMessageFormOpen: true});
        }
    }

    //
    // close the filter and the message form
    //
    closeForms = (callback) => {
        this.context.toggleFilter(null, false);
        this.setState({addMessageFormOpen: false, closeAll: true}, () => {
            this.setState({closeAll: false});
            if(callback && typeof callback === 'function'){
                callback();
            }
        });
    }

    //
    //
    applyFilter = (values) => {
        console.log("apply filter", values);
        this.setState({filterValues: values, messages: []}, () => {
            this.getData(() => {
                this.context.toggleFilter();
            })
        });
    }

    //
    // select a message to be marked for mass operations
    //
    onSelect = (index) => {
        // toggle the selection
        let selected = [];

        console.log("on select Message", index);

        if(this.state.selectedMessages.includes(index)){
            selected = this.state.selectedMessages.filter((v) => v !== index);
        }
        else{
            selected = [...this.state.selectedMessages, index].sort();
        }

        this.setState({selectedMessages: selected});
        this.context.toggleFilter(null, false);
    }

    //
    //
    deselectAll = () => {
        this.setState({selectedMessages: []});
    }

    //
    // mass operation to delete all selected messages
    //
    deleteAll = () => {
        // it'll be a Promise.allSettled

        const run = window.confirm("Are you sure you want to delete all of these messages?");

        if(run){
            const request = () => {
                console.log("run all hide requests");
                return Promise.allSettled(this.state.selectedMessages.map((index) => {
                    // need to pass the id of the message here
                    return deleteGeneralMessage(this.state.messages[index].messageID);
                }));
            };
    
            const then = (values) => {
    
                console.log("DELETE MESSAGES RETURN VALUES", values);
    
                // so what do I do with this list, which should be one-for-one with the selected indexes, in order?
                const remainingSelected = [];
                const messagesCopy = [...this.state.messages];
    
                // maybe remove the entries as we go from a copy
    
                // go from front to back, to make the splicing work more easily
                for(let i = values.length-1; i >= 0; i--){
                    const hasError = values[i].status === "rejected";
    
                    if(hasError){
                        const index = this.state.selectedMessages[i];
                        remainingSelected.push(index);
                    }
                    else{
                        const index = this.state.selectedMessages[i];
                        messagesCopy.splice(index, 1);
                    }
                }
    
                if(remainingSelected.length > 0){
                    // put it back in the right order
                    remainingSelected.sort();
                    this.context.sendNotification(true, "Not all messages successfully deleted");
                }
                else{
                    this.context.sendNotification(false, "Messages successfully deleted");
                }
    
                this.setState({messages: messagesCopy, selectedMessages: remainingSelected});
            };

            this.context.runProcessing(request, then, "deleteMessageMulti");
        }
    }

    //
    // mass operation to enable all selected messages
    //
    enableAll = () => {
        // look at the BusOps multi-select for this
        // it'll be a Promise.all

        const run = window.confirm("Are you sure you want to enable all of these messages?");

        if(run){
            const request = () => {
                console.log("run all enable messages");
                return Promise.allSettled(this.state.selectedMessages.map((index) => {
                    return enableOrDisableMessage(this.state.messages[index].messageID, true);
                }));
            };
    
            const then = (values) => {
    
                // so what do I do with this list, which should be one-for-one with the selected indexes, in order?
                const remainingSelected = [];
                const messagesCopy = [...this.state.messages];
    
                // maybe remove the entries as we go from a copy
    
                // go from front to back, to make the splicing work more easily
                for(let i = values.length-1; i >= 0; i--){
                    const hasError = values[i].status === "rejected";
    
                    if(hasError){
                        const index = this.state.selectedMessages[i];
                        remainingSelected.push(index);
                    }
                    else{
                        const index = this.state.selectedMessages[i];
                        messagesCopy[index].enable = "true";
                    }
                }
    
                if(remainingSelected.length > 0){
                    // put it back in the right order
                    remainingSelected.sort();
                    this.context.sendNotification(true, "Not all messages successfully enabled");
                }
                else{
                    this.context.sendNotification(false, "Messages successfully enabled");
                }
    
                this.setState({messages: messagesCopy, selectedMessages: remainingSelected});
            };

            this.context.runProcessing(request, then, "enableMessageMulti");
        }
    }

    //
    // mass operation to disable all messages
    //
    disableAll = () => {
        // look at the BusOps multi-select for this
        // it'll be a Promise.all

        const run = window.confirm("Are you sure you want to disable all of these messages?");

        if(run){
            const request = () => {
                console.log("run all enable messages");
                return Promise.allSettled(this.state.selectedMessages.map((index) => {
                    return enableOrDisableMessage(this.state.messages[index].messageID, false);
                }));
            };
    
            const then = (values) => {
    
                // so what do I do with this list, which should be one-for-one with the selected indexes, in order?
                const remainingSelected = [];
                const messagesCopy = [...this.state.messages];
    
                // maybe remove the entries as we go from a copy
    
                // go from front to back, to make the splicing work more easily
                for(let i = values.length-1; i >= 0; i--){
                    const hasError = values[i].status === "rejected";
    
                    if(hasError){
                        const index = this.state.selectedMessages[i];
                        remainingSelected.push(index);
                    }
                    else{
                        const index = this.state.selectedMessages[i];
                        messagesCopy[index].enable = "false";
                    }
                }
    
                if(remainingSelected.length > 0){
                    // put it back in the right order
                    remainingSelected.sort();
                    this.context.sendNotification(true, "Not all messages successfully disabled");
                }
                else{
                    this.context.sendNotification(false, "Messages successfully disabled");
                }
    
                this.setState({messages: messagesCopy, selectedMessages: remainingSelected});
            };

            this.context.runProcessing(request, then, "disableMessageMulti");
        }
    }

    //
    //
    addMessage = (newMessage) => {
        const request = () => addGeneralMessage(newMessage);
        const then = ({data}) => {
            console.log("ADD MESSAGE DATA", data);
            // ...
            if(data && (data.playnow || data.service_type)){
                console.log("MESSAGE ADDED", data);
                // "play now" messages don't get displayed in the list
                if(!data.playnow){
                    this.setState({
                        messages: addElement(this.state.messages, data)
                    });
                }

                this.closeForms(() => {
                    this.context.sendNotification(false, "New Message Added");
                })
            }
            else{
                this.context.sendNotification(true, "Add message failed, please try again");
            }
        }

        this.context.runProcessing(request, then, "addGeneralMessage");
    }

    //
    //
    updateMessage = (newMessage, index) => {
        const request = () => updateGeneralMessage(newMessage);
        const then = ({data}) => {
            // ...
            if(data){
                console.log("MESSAGE UPDATED", data);

                this.setState({
                    messages: replaceElement(this.state.messages, data, index)
                });

                this.closeForms(() => {
                    this.context.sendNotification(false, "Message Updated");
                });
            }
            else{
                this.context.sendNotification(true, "Update message failed, please try again");
            }
        }

        this.context.runProcessing(request, then, "updateGeneralMessage");
    }

    //
    //
    removeMessage = (index) => {

        const copy = this.state.messages.filter((message, i) => {
            return index !== i;
        });

        this.setState({messages: copy});
    }

    //
    //
    //
    //
    //
    render(){

        const hasSelection = this.state.selectedMessages.length > 0;

        console.log("MESSAGES", this.state.messages);

        return (
            <VerticalLayout firstHeight={this.context.filterOpen || this.state.addMessageFormOpen ? 0 : 8}>
                {
                    !this.context.filterOpen && !this.state.addMessageFormOpen
                    ?
                    <SubNav>
                    {
                        // mass operations on selected messages or else ass message button
                        hasSelection
                        ?   <>
                            <div className="dangerButton" onClick={this.deleteAll}>Delete All</div>
                            <div className="dangerButton" onClick={this.enableAll}>Enable All</div>
                            <div className="dangerButton" onClick={this.disableAll}>Disable All</div>
                            <div className="dangerButton" onClick={this.deselectAll}>Deselect All</div>
                            </>
                        : <div className="option  " onClick={this.openMessageForm}>
                            ADD NEW MESSAGE
                        </div>
                    }
                    </SubNav>
                    : null
                }
                <div className="content">
                    <>
                    {
                        // the filter
                        this.context.hasFilter
                        && this.context.filterOpen
                            ? <MessageGeneralFilter applyFilter={this.applyFilter} filterValues={this.state.filterValues} />
                            : null
                    }

                    {
                        // the add form
                        this.state.addMessageFormOpen
                        ? <div style={{marginBottom: "12px"}}>
                            <MessageGeneralForm submit={this.addMessage} locationLookup={this.state.locationLookup} />
                        </div>
                        : null
                    }

                    <SingleOpenList 
                        data={this.state.messages} 
                        component={MessageGeneralData} 
                        forceClose={this.state.closeAll || this.context.filterOpen || this.state.addMessageFormOpen || this.state.selectedMessages.length > 0}
                        prependCall={(mainFunc) => {
                            console.log("PREPEND CALL");
                            this.deselectAll();
                            this.closeForms(mainFunc);
                        }}
                        props={{
                            selectedList: this.state.selectedMessages, 
                            onSelect: this.onSelect,
                            updateMessage: this.updateMessage,
                            removeMessage: this.removeMessage,
                            locationLookup: this.state.locationLookup
                        }} 
                    />

                    </>
                </div>
            </VerticalLayout>
        )
    }
}